import React from 'react';
import { Card, Row, Col, Progress, Typography } from 'antd';
import { AimOutlined, CheckCircleOutlined } from '@ant-design/icons';
import AnalyticsCard from '../../components/shared/AnalyticsCard';
import ChartContainer from '../../components/shared/ChartContainer';
import { mockPerformanceData, mockOperationalMetrics } from '../data/mockData';

const { Text, Title } = Typography;

const GoalTracking: React.FC = () => {
  const { goals, summary } = mockPerformanceData;

  // Calculate goal achievement percentages
  const revenueProgress = (89200 / goals.revenueTarget) * 100;
  const clientProgress = (summary.totalClients / goals.clientTarget) * 100;
  const retentionProgress = (87.3 / goals.retentionTarget) * 100;
  const satisfactionProgress = (summary.avgSatisfactionScore / goals.satisfactionTarget) * 100;

  // Goal tracking chart data
  const goalTrackingData = {
    labels: ['Revenue', 'Clients', 'Retention', 'Satisfaction'],
    datasets: [
      {
        label: 'Current (%)',
        data: [revenueProgress, clientProgress, retentionProgress, satisfactionProgress],
        backgroundColor: [
          revenueProgress >= 100 ? '#52c41a' : revenueProgress >= 80 ? '#1890ff' : '#faad14',
          clientProgress >= 100 ? '#52c41a' : clientProgress >= 80 ? '#1890ff' : '#faad14',
          retentionProgress >= 100 ? '#52c41a' : retentionProgress >= 80 ? '#1890ff' : '#faad14',
          satisfactionProgress >= 100 ? '#52c41a' : satisfactionProgress >= 80 ? '#1890ff' : '#faad14',
        ],
      },
    ],
  };

  // Operational goals chart
  const operationalGoalsData = {
    labels: mockOperationalMetrics.map(item => item.metric.replace(' Rate', '').replace(' Time', '')),
    datasets: [
      {
        label: 'Current Value',
        data: mockOperationalMetrics.map(item => item.value),
        backgroundColor: 'rgba(24, 144, 255, 0.6)',
        borderColor: '#1890ff',
        borderWidth: 1,
      },
      {
        label: 'Target',
        data: mockOperationalMetrics.map(item => item.target),
        backgroundColor: 'rgba(82, 196, 26, 0.6)',
        borderColor: '#52c41a',
        borderWidth: 1,
      },
    ],
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 100) return '#52c41a';
    if (progress >= 80) return '#1890ff';
    if (progress >= 60) return '#faad14';
    return '#f5222d';
  };

  const getStatusText = (progress: number) => {
    if (progress >= 100) return 'Target Achieved';
    if (progress >= 80) return 'On Track';
    if (progress >= 60) return 'Needs Attention';
    return 'Critical';
  };

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <AnalyticsCard
            title="Monthly Revenue Goal"
            value={revenueProgress}
            format="percentage"
            trend={revenueProgress >= 90 ? 'up' : 'stable'}
            change={revenueProgress - 85}
            subtitle={`$${(89200).toLocaleString()} / $${goals.revenueTarget.toLocaleString()}`}
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="Client Target Goal"
            value={clientProgress}
            format="percentage"
            trend={clientProgress >= 90 ? 'up' : 'stable'}
            change={clientProgress - 85}
            subtitle={`${summary.totalClients} / ${goals.clientTarget} clients`}
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="Retention Goal"
            value={retentionProgress}
            format="percentage"
            trend={retentionProgress >= 90 ? 'up' : 'down'}
            change={retentionProgress - 90}
            subtitle={`87.3% / ${goals.retentionTarget}%`}
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="Satisfaction Goal"
            value={satisfactionProgress}
            format="percentage"
            trend={satisfactionProgress >= 90 ? 'up' : 'stable'}
            change={satisfactionProgress - 90}
            subtitle={`${summary.avgSatisfactionScore} / ${goals.satisfactionTarget}`}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <ChartContainer
            title={
              <span>
                <AimOutlined /> Goal Achievement Overview
              </span>
            }
            type="bar"
            data={goalTrackingData}
            height={300}
          />
        </Col>
        <Col span={12}>
          <ChartContainer
            title="Operational Goals vs Current Performance"
            type="bar"
            data={operationalGoalsData}
            height={300}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card 
            title={
              <span>
                <CheckCircleOutlined /> Goal Progress Summary
              </span>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>Monthly Revenue</Text>
                <Text strong style={{ color: getProgressColor(revenueProgress) }}>
                  {getStatusText(revenueProgress)}
                </Text>
              </div>
              <Progress 
                percent={revenueProgress} 
                strokeColor={getProgressColor(revenueProgress)}
                format={(percent) => `${percent?.toFixed(1)}%`}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>Client Count</Text>
                <Text strong style={{ color: getProgressColor(clientProgress) }}>
                  {getStatusText(clientProgress)}
                </Text>
              </div>
              <Progress 
                percent={clientProgress} 
                strokeColor={getProgressColor(clientProgress)}
                format={(percent) => `${percent?.toFixed(1)}%`}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>Retention Rate</Text>
                <Text strong style={{ color: getProgressColor(retentionProgress) }}>
                  {getStatusText(retentionProgress)}
                </Text>
              </div>
              <Progress 
                percent={retentionProgress} 
                strokeColor={getProgressColor(retentionProgress)}
                format={(percent) => `${percent?.toFixed(1)}%`}
              />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>Satisfaction Score</Text>
                <Text strong style={{ color: getProgressColor(satisfactionProgress) }}>
                  {getStatusText(satisfactionProgress)}
                </Text>
              </div>
              <Progress 
                percent={satisfactionProgress} 
                strokeColor={getProgressColor(satisfactionProgress)}
                format={(percent) => `${percent?.toFixed(1)}%`}
              />
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Operational Metrics Performance">
            {mockOperationalMetrics.map((metric, index) => (
              <div key={index} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text>{metric.metric}</Text>
                  <Text strong>{metric.value} / {metric.target}</Text>
                </div>
                <Progress 
                  percent={(metric.value / metric.target) * 100}
                  strokeColor={getProgressColor((metric.value / metric.target) * 100)}
                  showInfo={false}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                  <Text type="secondary">
                    {metric.trend === 'up' ? '↗' : metric.trend === 'down' ? '↘' : '→'} 
                    {metric.change > 0 ? '+' : ''}{metric.change}
                  </Text>
                  <Text type="secondary">
                    {getStatusText((metric.value / metric.target) * 100)}
                  </Text>
                </div>
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Goal Achievement Timeline">
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ margin: 0, color: '#52c41a' }}>Q1 2024</Title>
                  <Text type="secondary">Revenue Target: Achieved</Text>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ margin: 0, color: '#1890ff' }}>Q2 2024</Title>
                  <Text type="secondary">Client Growth: On Track</Text>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ margin: 0, color: '#faad14' }}>Q3 2024</Title>
                  <Text type="secondary">Retention: Needs Focus</Text>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ margin: 0, color: '#722ed1' }}>Q4 2024</Title>
                  <Text type="secondary">Satisfaction: Target Set</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default GoalTracking; 